import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Building2, Filter, Download, ExternalLink, Loader2, AlertCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentsPage = () => {
  const { token } = useAuth();
  
  const [companies, setCompanies] = useState([]);
  const [documentsByCompany, setDocumentsByCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCompanyId, setFilterCompanyId] = useState('all');
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const [mailScansByCompany, setMailScansByCompany] = useState({});
  const [activeServicesByCompany, setActiveServicesByCompany] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Fetch all companies for the user
        const companiesRes = await axios.get(`${API}/companies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const companiesList = companiesRes.data.companies || [];
        setCompanies(companiesList);
        
        // Fetch documents for each company
        const docsMap = {};
        
        await Promise.all(companiesList.map(async (company) => {
          try {
            const docsRes = await axios.get(`${API}/companies/${company.id}/documents`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            docsMap[company.id] = (docsRes.data.documents || []).filter(
              d => d.document_category !== 'ra_mail_scan' && d.document_category !== 'mail_forward_scan'
            );
          } catch (err) {
            console.error(`Error fetching docs for company ${company.id}:`, err);
            docsMap[company.id] = [];
          }
        }));
        
        setDocumentsByCompany(docsMap);

        // Fetch mail scans and detect active RA/MF services per company
        const scansMap = {};
        const servicesMap = {};

        await Promise.all(companiesList.map(async (company) => {
          try {
            const [scansRes, ordersRes] = await Promise.all([
              axios.get(`${API}/companies/${company.id}/mail-scans`, {
                headers: { Authorization: `Bearer ${token}` }
              }).catch(() => ({ data: { mail_scans: [] } })),
              axios.get(`${API}/orders?company_id=${company.id}&limit=100`, {
                headers: { Authorization: `Bearer ${token}` }
              }).catch(() => ({ data: { orders: [] } }))
            ]);

            scansMap[company.id] = scansRes.data.mail_scans || [];

            // Detect active RA/MF from orders
            const orders = ordersRes.data.orders || [];
            const svc = { ra: false, mf: false };
            for (const o of orders) {
              if (!['approved', 'completed'].includes(o.status)) continue;
              const st = o.service_type || '';
              const pn = (o.product_name || '').toLowerCase();
              if (st === 'registered_agent' || pn.includes('registered agent')) svc.ra = true;
              if (st === 'mail_forwarding' || pn.includes('mail forwarding')) svc.mf = true;
            }
            servicesMap[company.id] = svc;
          } catch {
            scansMap[company.id] = [];
            servicesMap[company.id] = { ra: false, mf: false };
          }
        }));

        setMailScansByCompany(scansMap);
        setActiveServicesByCompany(servicesMap);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = async (doc) => {
    setDownloadingDoc(doc.id);
    setDownloadError('');
    
    try {
      const res = await axios.get(`${API}/company-documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.download_url) {
        window.open(res.data.download_url, '_blank');
      } else {
        setDownloadError('Failed to get download URL');
      }
    } catch (err) {
      console.error('Download error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to download document';
      setDownloadError(errorMsg);
      
      // Clear error after 5 seconds
      setTimeout(() => setDownloadError(''), 5000);
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Filter companies based on selection
  const filteredCompanies = filterCompanyId === 'all' 
    ? companies 
    : companies.filter(c => c.id === filterCompanyId);

  // Calculate total documents (vault docs + mail scans)
  const totalVaultDocs = Object.values(documentsByCompany).reduce((sum, docs) => sum + docs.length, 0);
  const totalMailScans = Object.values(mailScansByCompany).reduce((sum, scans) => sum + scans.length, 0);
  const totalDocuments = totalVaultDocs + totalMailScans;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading documents...</span>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Download Error Alert */}
      {downloadError && (
        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-400 text-sm">{downloadError}</p>
        </div>
      )}
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Documents</h1>
          <p className="text-gray-400 text-sm mt-1">
            {totalDocuments} document{totalDocuments !== 1 ? 's' : ''} across {companies.length} compan{companies.length !== 1 ? 'ies' : 'y'}
          </p>
        </div>
        
        {/* Company Filter */}
        {companies.length > 1 && (
          <div className="mt-4 sm:mt-0 flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterCompanyId}
              onChange={(e) => setFilterCompanyId(e.target.value)}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#059669]"
              data-testid="company-filter"
            >
              <option value="all">All Companies</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.company_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {totalDocuments === 0 && (
        <div className="bg-[#111827] border border-gray-800 rounded-lg p-12 text-center" data-testid="empty-state">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-600" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">No Documents Yet</h2>
          <p className="text-gray-400 max-w-md mx-auto mb-6">
            Documents will appear here once uploaded against an order. 
            Start by placing an order for any service.
          </p>
          <Link
            to="/dashboard/orders/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white"
            style={{ backgroundColor: '#059669' }}
            data-testid="place-order-btn"
          >
            Place an Order
          </Link>
        </div>
      )}

      {/* Documents grouped by company */}
      {totalDocuments > 0 && (
        <div className="space-y-8">
          {filteredCompanies.map((company) => {
            const companyDocs = documentsByCompany[company.id] || [];
            const companyScans = mailScansByCompany[company.id] || [];
            const svc = activeServicesByCompany[company.id] || { ra: false, mf: false };
            const hasService = svc.ra || svc.mf;
            
            if (companyDocs.length === 0 && companyScans.length === 0 && !hasService && filterCompanyId === 'all') {
              return null; // Don't show companies with no docs, no scans, and no active mail service
            }
            
            return (
              <div key={company.id} data-testid={`company-docs-${company.id}`}>
                {/* Company Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#059669]" />
                  </div>
                  <div>
                    <Link 
                      to={`/dashboard/companies/${company.id}`}
                      className="text-white font-semibold hover:text-[#059669] transition-colors"
                    >
                      {company.company_name}
                    </Link>
                    <p className="text-gray-500 text-sm">
                      {company.state_of_formation} • {companyDocs.length + companyScans.length} document{(companyDocs.length + companyScans.length) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Documents Table */}
                {companyDocs.length > 0 ? (
                  <div className="bg-[#111827] border border-gray-800 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">
                            Document
                          </th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden sm:table-cell">
                            Type
                          </th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">
                            Size
                          </th>
                          <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">
                            Uploaded
                          </th>
                          <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {companyDocs.map((doc) => (
                          <tr key={doc.id} className="hover:bg-gray-800/30 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-gray-500 flex-shrink-0" />
                                <span className="text-white text-sm truncate max-w-[200px]">
                                  {doc.file_name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className="text-gray-400 text-sm capitalize">
                                {doc.document_type?.replace(/_/g, ' ') || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className="text-gray-400 text-sm">
                                {formatFileSize(doc.file_size) || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className="text-gray-400 text-sm">
                                {formatDate(doc.uploaded_at)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => handleDownload(doc)}
                                disabled={downloadingDoc === doc.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#059669] hover:bg-[#059669]/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`download-${doc.id}`}
                              >
                                {downloadingDoc === doc.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                                <span className="hidden sm:inline">
                                  {downloadingDoc === doc.id ? 'Loading...' : 'Download'}
                                </span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 text-center">
                    <p className="text-gray-500 text-sm">No documents uploaded for this company yet.</p>
                  </div>
                )}

                {/* Mail Scans Section */}
                {(() => {
                  if (!hasService) return null;

                  return (
                    <div className="mt-6" data-testid={`mail-scans-section-${company.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <h3 className="text-white font-semibold text-sm">Mail Scans</h3>
                      </div>
                      <p className="text-gray-500 text-xs mb-3">
                        Scans of physical mail received at our address on behalf of your company. Review promptly — mail from state authorities, the IRS, or legal senders may have response deadlines.
                      </p>

                      {companyScans.length === 0 ? (
                        <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 text-center" data-testid={`mail-scans-empty-${company.id}`}>
                          <Mail className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">No mail has been received yet. When we receive and scan mail on your behalf, it will appear here.</p>
                        </div>
                      ) : (
                        <div className="bg-[#111827] border border-gray-800 rounded-lg overflow-hidden" data-testid={`mail-scans-list-${company.id}`}>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-800">
                                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Sender</th>
                                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Received</th>
                                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Urgency</th>
                                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Type</th>
                                  <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800">
                                {companyScans.map((scan) => (
                                  <tr key={scan.id} className="hover:bg-gray-800/30 transition-colors" data-testid={`mail-scan-row-${scan.id}`}>
                                    <td className="px-4 py-3">
                                      <span className="text-white text-sm block">{scan.mail_sender}</span>
                                      {scan.mail_admin_note && (
                                        <span className="text-gray-500 text-xs italic block mt-0.5">{scan.mail_admin_note}</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                      <span className="text-gray-400 text-sm">{formatDate(scan.mail_received_date)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                      {scan.mail_urgency === 'urgent' ? (
                                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded" data-testid={`scan-urgency-${scan.id}`}>Urgent</span>
                                      ) : (
                                        <span className="px-2 py-0.5 bg-gray-600/30 text-gray-400 text-xs rounded">Routine</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                      <span className="text-gray-400 text-sm">
                                        {scan.document_category === 'ra_mail_scan' ? 'Registered Agent' : 'Mail Forwarding'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <button
                                        onClick={() => handleDownload(scan)}
                                        disabled={downloadingDoc === scan.id}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-[#059669] hover:bg-[#059669]/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-testid={`download-scan-${scan.id}`}
                                      >
                                        {downloadingDoc === scan.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Download className="w-4 h-4" />
                                        )}
                                        <span className="hidden sm:inline">
                                          {downloadingDoc === scan.id ? 'Loading...' : 'Download'}
                                        </span>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
};

export default DocumentsPage;
